"use client";

import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { formatTime } from "@/util/time";
import { useVideoPlayerContext } from "../context";

export const ClipRangeSelector = memo(() => {
  const context = useVideoPlayerContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef(0); // Time offset from pointer position to clip start

  const clipRange = context?.clipRange;
  const onClipRangeChange = context?.onClipRangeChange;
  const pause = context?.pause;
  const seekTo = context?.seekTo;
  const videoDuration = context?.duration ?? 0;
  const videoRef = context?.videoRef;

  const [playheadTime, setPlayheadTime] = useState(0);

  useEffect(() => {
    const videoElement = videoRef?.current;
    if (!videoElement) return;

    const updateTime = () => {
      setPlayheadTime(videoElement.currentTime);
    };

    // Initial sync
    updateTime();

    videoElement.addEventListener("timeupdate", updateTime);
    return () => {
      videoElement.removeEventListener("timeupdate", updateTime);
    };
  }, [videoRef]);

  const localStartTime = clipRange?.startTime ?? 0;
  const clipDuration = clipRange?.duration ?? 0;

  const clampStartTime = useCallback(
    (time: number) => {
      if (videoDuration <= 0) return 0;
      const maxStartTime = Math.max(0, videoDuration - clipDuration);
      return Math.min(Math.max(time, 0), maxStartTime);
    },
    [videoDuration, clipDuration]
  );

  // Ensure clip window stays within bounds when video duration or duration changes
  useEffect(() => {
    if (videoDuration > 0 && localStartTime + clipDuration > videoDuration) {
      const adjustedStartTime = Math.max(0, videoDuration - clipDuration);
      if (adjustedStartTime !== localStartTime) {
        onClipRangeChange?.(adjustedStartTime);
      }
    }
  }, [videoDuration, clipDuration, localStartTime, onClipRangeChange]);

  const leftPercent =
    videoDuration > 0 ? (localStartTime / videoDuration) * 100 : 0;
  const widthPercent =
    videoDuration > 0 ? Math.min((clipDuration / videoDuration) * 100, 100) : 0;

  // Playhead position relative to the local selection window
  const playheadPercent =
    clipDuration > 0
      ? ((playheadTime - localStartTime) / clipDuration) * 100
      : 0;

  const showPlayhead =
    playheadTime >= localStartTime &&
    playheadTime <= localStartTime + clipDuration;

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!containerRef.current || videoDuration <= 0) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pointerX = e.clientX - rect.left;
      const pointerTime = (pointerX / rect.width) * videoDuration;

      dragOffsetRef.current = pointerTime - localStartTime;
      setIsDragging(true);
      pause?.();
      seekTo?.(localStartTime);

      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      e.stopPropagation();
    },
    [localStartTime, pause, seekTo, videoDuration]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging || !containerRef.current || videoDuration <= 0) return;

      const rect = containerRef.current.getBoundingClientRect();
      const pointerX = e.clientX - rect.left;
      const pointerTime = (pointerX / rect.width) * videoDuration;

      const newStartTime = clampStartTime(pointerTime - dragOffsetRef.current);

      // 1. Update local UI immediately (60fps)
      // Since context.onClipRangeChange updates the Modal draftState immediately,
      // and we use the prop as our source, this creates a smooth loop.
      onClipRangeChange?.(newStartTime);
      seekTo?.(newStartTime);
    },
    [isDragging, clampStartTime, onClipRangeChange, seekTo, videoDuration]
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    setIsDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  }, []);

  if (!context || !clipRange) return null;

  return (
    <div
      ref={containerRef}
      data-testid="clip-range-container"
      className="relative flex items-center flex-1 ml-2 mr-1 h-4 group bg-white/15 rounded-[3px] overflow-hidden"
    >
      <div
        data-testid="clip-range-handle"
        className="absolute h-full rounded-[3px] cursor-ew-resize z-10 border-x-[6px] border-y-2 border-secondary text-white select-none touch-none"
        style={{
          left: `${leftPercent}%`,
          width: `${widthPercent}%`,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className="w-full h-full rounded-xs bg-[rgba(43,41,54,0.4)] backdrop-blur-xs outline-2 outline-secondary">
          <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[11px]">
            {formatTime(localStartTime)}
          </span>

          <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[11px]">
            {formatTime(localStartTime + clipDuration, { min: 1 })}
          </span>

          {/* Playhead Indicator (Internal) */}
          {showPlayhead && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white/30 z-20 pointer-events-none"
              style={{ left: `${playheadPercent}%` }}
            />
          )}
        </div>
      </div>

      {/* Total Duration Label on the Right */}
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-white select-none">
        {formatTime(videoDuration, { min: 1 })}
      </span>
    </div>
  );
});

ClipRangeSelector.displayName = "ClipRangeSelector";
